import { Routes, Route, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTelegramWebApp } from './hooks/useTelegramWebApp'
import { useAppStore } from './store/useAppStore'
import { useErrorHandler } from './hooks/useErrorHandler'
import {
  useCategories,
  useCards,
  useFavorites,
  useAnnouncements,
  useSubscription,
} from './hooks/useApi'
import LoadingScreen from './components/LoadingScreen/LoadingScreen'
import FortuneLoadingScreen from './components/FortuneLoadingScreen/FortuneLoadingScreen'
import ScrollToTop from './components/ScrollToTop'
import Layout from './components/Layout/Layout'
import HomePage from './pages/HomePage/HomePage'
import ProfilePage from './pages/ProfilePage/ProfilePage'
import CardPage from './pages/CardPage'
import TestVideoPage from './pages/TestVideoPage'
import SubscribePage from './pages/SubscribePage'
import CoursesPage from './pages/CoursesPage/CoursesPage'
import CourseDetailPage from './pages/CourseDetailPage/CourseDetailPage'
import LessonPage from './pages/LessonPage/LessonPage'
import FavoritesPage from './pages/FavoritesPage/FavoritesPage'
import FortuneWheelPage from './pages/FortuneWheelPage/FortuneWheelPage'
import './App.css'
import './index.css'

function App() {
  const location = useLocation()
  const { isLoading, startParam } = useTelegramWebApp()
  const { isLoading: storeLoading, authStatus } = useAppStore()
  const [showInitialLoading, setShowInitialLoading] = useState(true)

  // Инициализируем глобальную обработку ошибок
  useErrorHandler()

  // Предзагружаем основные данные только после завершения авторизации
  const isAuthComplete = authStatus === 'success'
  useCategories(isAuthComplete) // Загружаем категории после авторизации
  useCards() // Загружаем карточки после авторизации
  useFavorites() // Загружаем избранное после авторизации
  useAnnouncements() // Загружаем анонсы после авторизации
  useSubscription() // Загружаем информацию о подписке после авторизации

  // Автоматически скрываем экран загрузки через 3.5 секунды
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInitialLoading(false)
    }, 3500) // 3.5 секунды

    return () => clearTimeout(timer)
  }, [])

  // Функция для принудительного скрытия экрана загрузки при клике на кнопку
  const handleLoadingComplete = () => {
    setShowInitialLoading(false)
  }

  // Определяем, нужно ли показывать LoadingScreen
  const shouldShowLoading = showInitialLoading || isLoading || storeLoading

  // Определяем, какой экран загрузки использовать
  // Проверяем startParam='wheel' ИЛИ текущий путь fortune-wheel
  const isFortuneWheelRoute =
    startParam === 'wheel' || location.pathname.startsWith('/fortune-wheel')

  // Определяем, нужно ли применять safe-area-inset для текущего маршрута
  const shouldApplySafeArea =
    !location.pathname.startsWith('/card/') &&
    !location.pathname.startsWith('/subscribe') &&
    !location.pathname.match(/^\/courses\/\d/)

  return (
    <div className={shouldApplySafeArea ? 'safe-area-inset' : ''}>
      {/* Основное приложение всегда рендерится для выполнения запросов */}
      <div style={{ visibility: shouldShowLoading ? 'hidden' : 'visible' }}>
        <ScrollToTop />
        <Routes>
          {/* Страницы без Layout */}
          <Route path="card/:cardId" element={<CardPage />} />
          <Route path="courses/:courseId" element={<CourseDetailPage />} />
          <Route path="courses/:courseId/lesson/:lessonId" element={<LessonPage />} />
          <Route path="test-video" element={<TestVideoPage />} />
          <Route path="subscribe" element={<SubscribePage />} />
          {/* <Route path="fortune-wheel" element={<FortuneWheelPage />} /> */}

          {/* Страницы с Layout (Header + BottomMenu) */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="favorites" element={<FavoritesPage />} />
          </Route>
        </Routes>
      </div>

      {/* LoadingScreen показывается поверх основного приложения */}
      {shouldShowLoading &&
        (isFortuneWheelRoute ? (
          <FortuneLoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ) : (
          <LoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ))}
    </div>
  )
}

export default App
